/**
 * Evaluation API Service
 *
 * Handles communication with the backend for evaluation question management.
 * Automatically attaches Firebase auth tokens when available.
 */

import { API_BASE, getAuthHeaders, fetchApi } from './apiClient';

export interface EvalQuestion {
  id: number;
  question: string;
  question_type: string | null;
  difficulty: string | null;
  expected_answer: string | null;
  source_chunk_ids: string[];
  source_doc_titles: string[];
  source_doc_path: string | null;
  expected_module: string | null;
  generated_from_chunk_id: string | null;
  generated_by: string | null;
  is_reviewed: boolean;
  is_approved: boolean;
  reviewer_notes: string | null;
  last_eval_at: string | null;
  last_eval_response: string | null;
  last_eval_sources: Array<Record<string, unknown>> | null;
  last_eval_confidence: number | null;
  retrieval_hit: boolean | null;
  answer_quality: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface EvalStats {
  total: number;
  reviewed: number;
  approved: number;
  evaluated: number;
  by_type: Record<string, number>;
  by_module: Record<string, number>;
  by_quality: Record<string, number>;
}

export interface EvalQuestionUpdate {
  question?: string;
  question_type?: string;
  difficulty?: string;
  expected_answer?: string;
  source_chunk_ids?: string[];
  source_doc_titles?: string[];
  expected_module?: string;
  is_reviewed?: boolean;
  is_approved?: boolean;
  reviewer_notes?: string;
}

class EvaluationApi {
  private baseUrl: string;

  constructor() {
    this.baseUrl = `${API_BASE}/evaluation`;
  }

  /**
   * Get evaluation questions with optional filters
   */
  async getQuestions(params: {
    types?: string;
    modules?: string;
    reviewed_only?: boolean;
    approved_only?: boolean;
    limit?: number;
    offset?: number;
  } = {}): Promise<EvalQuestion[]> {
    const searchParams = new URLSearchParams();
    if (params.types) searchParams.set('types', params.types);
    if (params.modules) searchParams.set('modules', params.modules);
    if (params.reviewed_only) searchParams.set('reviewed_only', 'true');
    if (params.approved_only) searchParams.set('approved_only', 'true');
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.offset) searchParams.set('offset', params.offset.toString());

    const url = `${this.baseUrl}/questions${searchParams.toString() ? `?${searchParams}` : ''}`;
    const headers = await getAuthHeaders();
    const response = await fetchApi(url, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get questions: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get a single evaluation question
   */
  async getQuestion(questionId: number): Promise<EvalQuestion> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/questions/${questionId}`, { headers });

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Question not found');
      }
      throw new Error(`Failed to get question: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Update an evaluation question
   */
  async updateQuestion(
    questionId: number,
    updates: EvalQuestionUpdate
  ): Promise<EvalQuestion> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/questions/${questionId}`, {
      method: 'PATCH',
      headers,
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      throw new Error(`Failed to update question: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Delete an evaluation question
   */
  async deleteQuestion(questionId: number): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/questions/${questionId}`, {
      method: 'DELETE',
      headers,
    });

    if (!response.ok && response.status !== 404) {
      throw new Error(`Failed to delete question: ${response.statusText}`);
    }
  }

  /**
   * Approve an evaluation question
   */
  async approveQuestion(questionId: number): Promise<void> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/questions/${questionId}/approve`, {
      method: 'POST',
      headers,
    });

    if (!response.ok) {
      throw new Error(`Failed to approve question: ${response.statusText}`);
    }
  }

  /**
   * Reject an evaluation question
   */
  async rejectQuestion(questionId: number, notes?: string): Promise<void> {
    const searchParams = new URLSearchParams();
    if (notes) searchParams.set('notes', notes);

    const url = `${this.baseUrl}/questions/${questionId}/reject${searchParams.toString() ? `?${searchParams}` : ''}`;
    const headers = await getAuthHeaders();
    const response = await fetchApi(url, { method: 'POST', headers });

    if (!response.ok) {
      throw new Error(`Failed to reject question: ${response.statusText}`);
    }
  }

  /**
   * Bulk approve questions
   */
  async bulkApprove(questionIds: number[]): Promise<{ count: number; question_ids: number[] }> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/questions/bulk-approve`, {
      method: 'POST',
      headers,
      body: JSON.stringify(questionIds),
    });

    if (!response.ok) {
      throw new Error(`Failed to bulk approve: ${response.statusText}`);
    }

    return response.json();
  }

  /**
   * Get evaluation statistics
   */
  async getStats(): Promise<EvalStats> {
    const headers = await getAuthHeaders();
    const response = await fetchApi(`${this.baseUrl}/stats`, { headers });

    if (!response.ok) {
      throw new Error(`Failed to get stats: ${response.statusText}`);
    }

    return response.json();
  }
}

export const evaluationApi = new EvaluationApi();
